// @ts-nocheck — mechanically-converted CommonJS; the upstream uses
// dynamic `this` and string-keyed leaflet extension that fights TS
// strictness. We trust the upstream's 2.2.0 runtime contract.
// Vendored from npm package leaflet-side-by-side@2.2.0 (2026-05-25).
// The upstream package lists css-img-datauri-stream, cssify, underscore,
// browserify-shim, etc. as runtime `dependencies` even though index.js
// never imports them — they're stale build-tool artifacts the maintainer
// mis-classified. Tree-shaking eliminated them from our prod bundle, but
// `npm audit` still reported 14 vulnerabilities (3 critical) from the
// transitive chain. Vendoring this single file (~5 KB) drops the entire
// dep tree to zero — see docs/security-audit-2026-05-25.md.
//
// Source: https://github.com/digidem/leaflet-side-by-side  (MIT)
// Mechanical conversion: CommonJS `require` → ESM `import`.
//
// PATCH (2026-06-19): multi-layer clipping. Upstream 2.2.0 tracks only a
// single `_leftLayer`/`_rightLayer` (the LAST on-map member of each array)
// and clips just that one — so a Map-Beast layer GROUP (group_id → several
// member layers activated as one swipe side) left every member but the last
// rendered full-width, ignoring the divider. We now clip EVERY on-map member
// of `_leftLayers`/`_rightLayers` (and clear them all on remove). The single
// `_leftLayer`/`_rightLayer` is kept only for the left/right layer add/remove
// events. This diverges from upstream behaviour intentionally.
import L from "leaflet";
import "./layout.css";
import "./range.css";

let mapWasDragEnabled: boolean;
let mapWasTapEnabled: boolean;

function getRangeEvent(rangeInput: HTMLInputElement): "input" | "change" {
  return "oninput" in rangeInput ? "input" : "change";
}

function cancelMapDrag(this: any) {
  mapWasDragEnabled = this._map.dragging.enabled();
  mapWasTapEnabled = this._map.tap && this._map.tap.enabled();
  this._map.dragging.disable();
  this._map.tap && this._map.tap.disable();
}

function uncancelMapDrag(this: any, e: any) {
  this._refocusOnMap(e);
  if (mapWasDragEnabled) this._map.dragging.enable();
  if (mapWasTapEnabled) this._map.tap.enable();
}

function asArray(arg: any): any[] {
  return arg === undefined ? [] : Array.isArray(arg) ? arg : [arg];
}

function noop() {}

(L.Control as any).SideBySide = L.Control.extend({
  options: {
    thumbSize: 42,
    padding: 0,
  },

  initialize: function (leftLayers: any, rightLayers: any, options: any) {
    this.setLeftLayers(leftLayers);
    this.setRightLayers(rightLayers);
    L.setOptions(this, options);
  },

  getPosition: function () {
    const rangeValue = parseFloat(this._range.value);
    const offset = (0.5 - rangeValue) * (2 * this.options.padding + this.options.thumbSize);
    return this._map.getSize().x * rangeValue + offset;
  },

  setPosition: noop,

  includes: L.Evented.prototype || (L as any).Mixin.Events,

  addTo: function (map: any) {
    this.remove();
    this._map = map;

    const container = (this._container = L.DomUtil.create(
      "div",
      "leaflet-sbs",
      map._controlContainer,
    ));

    this._divider = L.DomUtil.create("div", "leaflet-sbs-divider", container);
    const range = (this._range = L.DomUtil.create(
      "input",
      "leaflet-sbs-range",
      container,
    ) as HTMLInputElement);
    range.type = "range";
    range.min = "0";
    range.max = "1";
    range.step = "any";
    range.value = "0.5";
    range.style.paddingLeft = range.style.paddingRight = this.options.padding + "px";
    this._addEvents();
    this._updateLayers();
    return this;
  },

  remove: function () {
    if (!this._map) return this;
    // clear the clip on EVERY member of both sides (not just the representative)
    const clear = (layers: any[]) =>
      (layers || []).forEach((layer: any) => {
        const c = layer && layer.getContainer && layer.getContainer();
        if (c) c.style.clip = "";
      });
    clear(this._leftLayers);
    clear(this._rightLayers);
    this._removeEvents();
    L.DomUtil.remove(this._container);
    this._map = null;
    return this;
  },

  setLeftLayers: function (leftLayers: any) {
    this._leftLayers = asArray(leftLayers);
    this._updateLayers();
    return this;
  },

  setRightLayers: function (rightLayers: any) {
    this._rightLayers = asArray(rightLayers);
    this._updateLayers();
    return this;
  },

  _updateClip: function () {
    const map = this._map;
    const nw = map.containerPointToLayerPoint([0, 0]);
    const se = map.containerPointToLayerPoint(map.getSize());
    const clipX = nw.x + this.getPosition();
    const dividerX = this.getPosition();

    this._divider.style.left = dividerX + "px";
    this.fire("dividermove", { x: dividerX });
    const clipLeft = "rect(" + [nw.y, clipX, se.y, nw.x].join("px,") + "px)";
    const clipRight = "rect(" + [nw.y, se.x, se.y, clipX].join("px,") + "px)";
    // clip EVERY on-map member of each side, so a multi-layer group swipes as one
    const apply = (layers: any[], clip: string) =>
      (layers || []).forEach((layer: any) => {
        if (!this._map.hasLayer(layer)) return;
        const c = layer.getContainer && layer.getContainer();
        if (c) c.style.clip = clip;
      });
    apply(this._leftLayers, clipLeft);
    apply(this._rightLayers, clipRight);
  },

  _updateLayers: function () {
    if (!this._map) return this;
    const prevLeft = this._leftLayer;
    const prevRight = this._rightLayer;
    this._leftLayer = this._rightLayer = null;
    this._leftLayers.forEach(function (this: any, layer: any) {
      if (this._map.hasLayer(layer)) this._leftLayer = layer;
    }, this);
    this._rightLayers.forEach(function (this: any, layer: any) {
      if (this._map.hasLayer(layer)) this._rightLayer = layer;
    }, this);
    if (prevLeft !== this._leftLayer) {
      prevLeft && this.fire("leftlayerremove", { layer: prevLeft });
      this._leftLayer && this.fire("leftlayeradd", { layer: this._leftLayer });
    }
    if (prevRight !== this._rightLayer) {
      prevRight && this.fire("rightlayerremove", { layer: prevRight });
      this._rightLayer && this.fire("rightlayeradd", { layer: this._rightLayer });
    }
    this._updateClip();
  },

  _addEvents: function () {
    const range = this._range;
    const map = this._map;
    if (!map || !range) return;
    map.on("move", this._updateClip, this);
    map.on("layeradd layerremove", this._updateLayers, this);
    L.DomEvent.on(range, getRangeEvent(range), this._updateClip, this);
    L.DomEvent.on(range, "touchstart", cancelMapDrag, this);
    L.DomEvent.on(range, "touchend", uncancelMapDrag, this);
    L.DomEvent.on(range, "mousedown", cancelMapDrag, this);
    L.DomEvent.on(range, "mouseup", uncancelMapDrag, this);
  },

  _removeEvents: function () {
    const range = this._range;
    const map = this._map;
    if (range) {
      L.DomEvent.off(range, getRangeEvent(range), this._updateClip, this);
      L.DomEvent.off(range, "touchstart", cancelMapDrag, this);
      L.DomEvent.off(range, "touchend", uncancelMapDrag, this);
      L.DomEvent.off(range, "mousedown", cancelMapDrag, this);
      L.DomEvent.off(range, "mouseup", uncancelMapDrag, this);
    }
    if (map) {
      map.off("layeradd layerremove", this._updateLayers, this);
      map.off("move", this._updateClip, this);
    }
  },
});

(L.control as any).sideBySide = function (leftLayers: any, rightLayers: any, options: any) {
  return new (L.Control as any).SideBySide(leftLayers, rightLayers, options);
};

export default (L.Control as any).SideBySide;
